import * as React from 'react';

import { Divider, Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { ProfitType } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  AppliedSellRateRule,
  PricingFormula,
  SellRateFlatAmountUnit,
  SellRateFuelMode,
  SellRatePricingMode,
  SellRateRoundingRules,
} from 'types/agents/QuickQuoteAgent';
import { cn } from 'utils/shadcn';

import { formatDollarsNoCents } from '../utils';
import {
  formatDistanceMiles,
  getFixedFinalPricingMathLabel,
  isFixedFinalPricing,
} from './helpers';

interface SellRateRuleBreakdownProps {
  buyRate?: number;
  sellRate?: number;
  sellRateRounded?: number;
  appliedSellRateRule?: AppliedSellRateRule;
  /** Fallback margin info from the sell-rate task when no rule matched. */
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  pricingMode?: SellRatePricingMode;
  roundingRules?: SellRateRoundingRules;
}

/** Format a dollar delta with explicit sign so adjustments read as math. */
function signedDollars(value: number): string {
  const rounded = Math.round(value);
  if (rounded > 0) {
    return `+$${rounded}`;
  }
  if (rounded < 0) {
    return `−$${Math.abs(rounded)}`;
  }
  return '$0';
}

function deltaColorClass(value: number): string {
  const rounded = Math.round(value);
  if (rounded > 0) {
    return 'text-success-main';
  }
  if (rounded < 0) {
    return 'text-error-main';
  }
  return 'text-neutral-500';
}

/**
 * Build the pricing description line for the math ledger, e.g.
 *   "$2.00/mi markup × 53 mi"
 *   "$250 flat markup"
 *   "5.0% markup of $629"
 *   "20.0% margin"
 */
function getPricingMathLabel(opts: {
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  flatAmountUnit?: SellRateFlatAmountUnit;
  distanceMiles?: number;
  baseRate?: number;
}): string {
  const formula = opts.pricingFormula ?? PricingFormula.Markup;
  const isFlat = opts.marginType === ProfitType.Amount;
  const value = opts.marginValue ?? 0;

  if (isFlat) {
    if (opts.flatAmountUnit === SellRateFlatAmountUnit.PerMile) {
      const miles =
        opts.distanceMiles && opts.distanceMiles > 0
          ? ` × ${Math.round(opts.distanceMiles)} mi`
          : '';
      return `$${value.toFixed(2)}/mi ${formula}${miles}`;
    }
    return `$${value.toFixed(0)} flat ${formula}`;
  }

  if (formula === PricingFormula.Margin) {
    return `${value.toFixed(1)}% margin`;
  }

  const baseSuffix =
    opts.baseRate != null ? ` of ${formatDollarsNoCents(opts.baseRate)}` : '';
  return `${value.toFixed(1)}% markup${baseSuffix}`;
}

type RowVariant = 'default' | 'subtotal' | 'final';

const Row = ({
  label,
  value,
  variant = 'default',
  delta,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  variant?: RowVariant;
  delta?: number;
}) => {
  const isStrong = variant === 'final' || variant === 'subtotal';
  const isDelta = delta !== undefined;

  return (
    <Flex align='center' justify='between' className='w-full gap-3'>
      <Typography
        variant='body-xxs'
        weight={isStrong ? 'semibold' : undefined}
        textColor={isStrong ? undefined : 'lessMuted'}
        className='min-w-0 flex-1 leading-snug'
      >
        {label}
      </Typography>
      <Typography
        variant='body-xxs'
        weight={isStrong || isDelta ? 'semibold' : 'medium'}
        className={cn(
          'shrink-0 tabular-nums',
          isDelta && deltaColorClass(delta)
        )}
      >
        {value}
      </Typography>
    </Flex>
  );
};

/**
 * Walks the sell-rate audit history as a small math ledger:
 *
 *   Buy Rate                   $629
 *   $2.00/mi markup × 53 mi   +$106
 *   Provider fuel surcharge    +$50
 *   Subtotal                   $785
 *   Rounded up to nearest $25  +$15
 *   ──────────────────────────────
 *   Final Sell Rate            $800
 *
 * Each adjustment is rendered as an explicit signed delta so the user can
 * verify how the agent arrived at the final sell rate.
 */
export const SellRateRuleBreakdown = ({
  buyRate,
  sellRate,
  sellRateRounded,
  appliedSellRateRule,
  marginType,
  marginValue,
  pricingFormula,
  pricingMode,
  roundingRules,
}: SellRateRuleBreakdownProps): Maybe<React.JSX.Element> => {
  const finalSellRate: MaybeUndef<number> =
    sellRateRounded != null ? sellRateRounded : sellRate;

  if (finalSellRate == null) {
    return null;
  }

  const startingBuyRate = appliedSellRateRule?.rateBeforeRule ?? buyRate;
  const isFixedFinal = isFixedFinalPricing({
    pricingMode,
    appliedSellRateRule,
    marginType,
    marginValue,
    pricingFormula,
    sellRate,
    buyRate,
  });

  const ruleHeader = appliedSellRateRule
    ? appliedSellRateRule.ruleName
      ? `Rule: "${appliedSellRateRule.ruleName}"`
      : 'Sell rate rule applied'
    : 'Default agent pricing';

  const effectivePricingType = appliedSellRateRule?.marginType ?? marginType;
  const effectivePricingValue = appliedSellRateRule?.marginValue ?? marginValue;
  const effectivePricingFormula =
    appliedSellRateRule?.pricingFormula ?? pricingFormula;

  const pricingLabel = getPricingMathLabel({
    marginType: effectivePricingType,
    marginValue: effectivePricingValue,
    pricingFormula: effectivePricingFormula,
    flatAmountUnit: appliedSellRateRule?.flatAmountUnit,
    distanceMiles: appliedSellRateRule?.distanceMiles,
    baseRate: startingBuyRate,
  });

  const showRoundingRow =
    sellRateRounded != null &&
    sellRate != null &&
    sellRateRounded !== sellRate &&
    roundingRules?.roundToNearest != null;
  const roundingDelta =
    showRoundingRow && sellRateRounded != null && sellRate != null
      ? sellRateRounded - sellRate
      : undefined;

  if (isFixedFinal) {
    const fixedFinalRate =
      appliedSellRateRule?.marginValue ?? marginValue ?? undefined;
    const fixedFinalDistance = appliedSellRateRule?.distanceMiles;
    const showPerMileAudit =
      appliedSellRateRule?.flatAmountUnit === SellRateFlatAmountUnit.PerMile &&
      fixedFinalRate != null &&
      fixedFinalDistance != null &&
      fixedFinalDistance > 0;
    const fixedFinalMathLabel = getFixedFinalPricingMathLabel({
      pricingMode: SellRatePricingMode.FixedFinal,
      appliedSellRateRule,
      marginType,
      marginValue,
      pricingFormula,
    });
    const fixedFinalLabel = fixedFinalMathLabel
      ? `Fixed final pricing (${fixedFinalMathLabel})`
      : 'Fixed final pricing';

    return (
      <Flex direction='col' gap='xs' className='w-full'>
        <Typography
          variant='body-xxs'
          weight='semibold'
          className='leading-snug text-neutral-900'
        >
          {ruleHeader}
        </Typography>

        <Divider />

        {showPerMileAudit ? (
          <>
            <Row label='Rate' value={`$${fixedFinalRate.toFixed(2)}/mi`} />
            <Row
              label='Distance'
              value={`${formatDistanceMiles(fixedFinalDistance)} mi`}
            />
            <Row
              label='Rate x Distance'
              value={formatDollarsNoCents(sellRate ?? finalSellRate)}
              variant='subtotal'
            />
          </>
        ) : (
          <Row
            label={fixedFinalLabel}
            value={formatDollarsNoCents(sellRate ?? finalSellRate)}
            variant='subtotal'
          />
        )}

        {showRoundingRow && roundingDelta != null && (
          <Row
            label={`Rounded ${roundingRules?.roundUp ? 'up' : 'down'} to nearest $${roundingRules?.roundToNearest?.toFixed(0)}`}
            value={signedDollars(roundingDelta)}
            delta={roundingDelta}
          />
        )}

        <Divider />

        <Row
          label='Final Quote'
          value={formatDollarsNoCents(finalSellRate)}
          variant='final'
        />
      </Flex>
    );
  }

  // Pricing delta: prefer rule-derived intermediate, otherwise infer it from
  // the same subtotal shown in the ledger. If fuel is also shown separately,
  // remove it from the inferred pricing delta so the rows add up.
  const rateAfterPricing = appliedSellRateRule?.rateAfterPricing;
  const rateAfterFuel = appliedSellRateRule?.rateAfterFuel;
  const fuelAmount = appliedSellRateRule?.fuelAmount;
  const showFuelRow = fuelAmount != null && fuelAmount > 0;
  const subtotalValue = rateAfterFuel ?? sellRate;

  let pricingDelta: Undef<number>;
  if (rateAfterPricing != null && startingBuyRate != null) {
    pricingDelta = rateAfterPricing - startingBuyRate;
  } else if (subtotalValue != null && startingBuyRate != null) {
    pricingDelta =
      subtotalValue - startingBuyRate - (showFuelRow ? fuelAmount : 0);
  }

  const showSubtotalRow = showFuelRow && subtotalValue != null;

  return (
    <Flex direction='col' gap='xs' className='w-full'>
      <Typography
        variant='body-xxs'
        weight='semibold'
        className='leading-snug text-neutral-900'
      >
        {ruleHeader}
      </Typography>

      <Divider />

      {startingBuyRate != null && (
        <Row label='Buy Rate' value={formatDollarsNoCents(startingBuyRate)} />
      )}

      {pricingDelta != null && (
        <Row
          label={pricingLabel}
          value={signedDollars(pricingDelta)}
          delta={pricingDelta}
        />
      )}

      {showFuelRow && fuelAmount != null && (
        <Row
          label={
            appliedSellRateRule?.fuelMode === SellRateFuelMode.Provider
              ? 'Provider fuel surcharge'
              : 'Fuel surcharge'
          }
          value={signedDollars(fuelAmount)}
          delta={fuelAmount}
        />
      )}

      {showSubtotalRow && subtotalValue != null && (
        <Row
          label='Subtotal'
          value={formatDollarsNoCents(subtotalValue)}
          variant='subtotal'
        />
      )}

      {showRoundingRow && roundingDelta != null && (
        <Row
          label={`Rounded ${roundingRules?.roundUp ? 'up' : 'down'} to nearest $${roundingRules?.roundToNearest?.toFixed(0)}`}
          value={signedDollars(roundingDelta)}
          delta={roundingDelta}
        />
      )}

      <Divider />

      <Row
        label='Final Sell Rate'
        value={formatDollarsNoCents(finalSellRate)}
        variant='final'
      />
    </Flex>
  );
};
